import { Flex, Typography, Slider, ConfigProvider, Spin } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactPlayer from 'react-player/file';
import Icon from '@ant-design/icons';
import { ReactComponent as Play } from '../../assets/icons/play.svg';
import { ReactComponent as PlayBlack } from '../../assets/icons/play-black.svg';
import { ReactComponent as Pause } from '../../assets/icons/pause.svg';
import { ReactComponent as PauseBlack } from '../../assets/icons/pause-black.svg';
import { ReactComponent as Volume } from '../../assets/icons/volume.svg';
import { ReactComponent as VolumeBlack } from '../../assets/icons/volume-black.svg';
import { ReactComponent as VolumeOff } from '../../assets/icons/volume-off.svg';
import { ReactComponent as VolumeOffBlack } from '../../assets/icons/volume-off-black.svg';
import { ReactComponent as Replay } from '../../assets/icons/replay.svg';
import { ReactComponent as ReplayBlack } from '../../assets/icons/replay-black.svg';
import { useTheme } from '../../context/ThemeContext';
import './VideoPlayer.css';
import { DARK } from '../../utilities/constants';

const { Text } = Typography;
const MARKER_FILL_SPAN_SECONDS = 3;
const MARKER_FILL_COLOR = '#ED0007';
const MIN_PLAYED_RATIO = 0.0001;
const DEFAULT_SLIDER_HIGHLIGHT = 'none';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const buildMarkerHighlightRanges = (markers, duration) => {
  if (duration <= 0 || !Array.isArray(markers) || markers.length === 0) {
    return [];
  }

  const ranges = markers
    .map(marker => Number(marker))
    .filter(Number.isFinite)
    .map(time => ({
      start: clamp(time - MARKER_FILL_SPAN_SECONDS, 0, duration),
      end: clamp(time + MARKER_FILL_SPAN_SECONDS, 0, duration),
    }))
    .filter(range => range.end > range.start)
    .sort((firstRange, secondRange) => firstRange.start - secondRange.start);

  return ranges.reduce((mergedRanges, currentRange) => {
    const previousRange = mergedRanges[mergedRanges.length - 1];

    if (!previousRange || currentRange.start > previousRange.end) {
      mergedRanges.push(currentRange);
      return mergedRanges;
    }

    previousRange.end = Math.max(previousRange.end, currentRange.end);
    return mergedRanges;
  }, []);
};

const buildGradientStopsForRange = (
  range,
  duration,
  currentPosition,
  transparentColor,
  highlightColor
) => {
  const stops = [];
  const startPercentage = (range.start / duration) * 100;
  const endPercentage = (range.end / duration) * 100;

  if (startPercentage > currentPosition) {
    stops.push(
      `${transparentColor} ${currentPosition}%`,
      `${transparentColor} ${startPercentage}%`
    );
  }

  stops.push(
    `${highlightColor} ${startPercentage}%`,
    `${highlightColor} ${endPercentage}%`
  );

  return { stops, endPercentage };
};

const createSliderHighlightGradient = (ranges, duration) => {
  if (duration <= 0 || ranges.length === 0) {
    return DEFAULT_SLIDER_HIGHLIGHT;
  }

  const highlightColor = 'var(--video-marker-color, #ED0007)';
  const transparentColor = 'transparent';
  const gradientStops = [`${transparentColor} 0%`];
  let currentPosition = 0;

  ranges.forEach(range => {
    const { stops, endPercentage } = buildGradientStopsForRange(
      range,
      duration,
      currentPosition,
      transparentColor,
      highlightColor
    );
    gradientStops.push(...stops);
    currentPosition = endPercentage;
  });

  if (currentPosition < 100) {
    gradientStops.push(
      `${transparentColor} ${currentPosition}%`,
      `${transparentColor} 100%`
    );
  }

  return `linear-gradient(to right, ${gradientStops.join(', ')})`;
};

const getPlayPauseIcon = (playing, theme) => {
  if (playing) return theme === DARK ? Pause : PauseBlack;
  return theme === DARK ? Play : PlayBlack;
};

const getVolumeIcon = (muted, theme) => {
  if (muted) return theme === DARK ? VolumeOff : VolumeOffBlack;
  return theme === DARK ? Volume : VolumeBlack;
};

const getPlayerContainerStyle = (url, height, width) => {
  if (url === '') {
    return {
      position: 'relative',
      height,
      width: width === 'auto' ? '100%' : width,
    };
  }
  return { position: 'relative' };
};

const VideoPlayer = ({
  url = '',
  height = '30vh',
  width = 'auto',
  sliderWidth = 250,
  setIsPlayClicked = () => {},
  markers = [],
  showMarkers = false,
  shouldPauseVideo = false,
  showOverlay = false,
  Overlay = <></>,
  feature = '',
  disableVolume = true,
  fixedContentWidth = false,
}) => {
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume] = useState(0.8);
  const [muted, setMuted] = useState(disableVolume);
  const [played, setPlayed] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [sliderMarks, setSliderMarks] = useState({});
  const [sliderHighlightGradient, setSliderHighlightGradient] = useState(
    DEFAULT_SLIDER_HIGHLIGHT
  );
  const [sliderRailBorderGradient, setSliderRailBorderGradient] =
    useState('none');
  const [loading, setLoading] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const [playerKey, setPlayerKey] = useState(Date.now());
  const { theme } = useTheme();

  const formatTime = useCallback(seconds => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const handlePlayPause = useCallback(() => {
    setPlaying(prev => !prev);
  }, []);

  const handleMute = useCallback(() => {
    if (disableVolume) {
      return;
    }
    setMuted(prev => !prev);
  }, [disableVolume]);

  const handleSeekChange = useCallback(
    value => {
      const seekTo = Number.parseFloat(value) * duration;
      playerRef.current?.seekTo(seekTo);
      setPlayed(Number.parseFloat(value));
    },
    [duration]
  );

  const handleDuration = useCallback(duration => {
    setDuration(duration);
  }, []);

  const handleProgress = useCallback(progress => {
    setPlayed(progress.played);
    setCurrentTime(progress.playedSeconds);
  }, []);

  const handleReplay = useCallback(() => {
    playerRef.current?.seekTo(0);
    setPlaying(true);
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (shouldPauseVideo) {
      setPlaying(false);
    }
  }, [shouldPauseVideo]);

  useEffect(() => {
    if (playing) {
      setIsPlayClicked(true);
    }
  }, [playing, setIsPlayClicked]);

  useEffect(() => {
    setMuted(disableVolume);
  }, [disableVolume]);

  useEffect(() => {
    if (url) {
      // Clear the ref before remounting
      playerRef.current = null;
      setLoading(true);
      setBuffering(false);
      setPlaying(false);
      setPlayed(0);
      setCurrentTime(0);
      setDuration(0);
      setSliderMarks({});
      setSliderHighlightGradient(DEFAULT_SLIDER_HIGHLIGHT);
      setPlayerKey(Date.now());
    }
  }, [url]);

  useEffect(() => {
    return () => {
      setPlaying(false);
      if (playerRef.current) {
        playerRef.current.seekTo(0);
      }
    };
  }, []);

  const buildBorderGradient = (highlightRanges, duration) => {
    if (highlightRanges.length === 0) return 'none';

    const borderStops = [];
    let currentPosition = 0;

    highlightRanges.forEach(range => {
      const startPercentage = (range.start / duration) * 100;
      const endPercentage = (range.end / duration) * 100;

      if (startPercentage > currentPosition) {
        borderStops.push(
          `var(--slider-rail-color, #EFF1F2) ${currentPosition}%`,
          `var(--slider-rail-color, #EFF1F2) ${startPercentage}%`
        );
      }
      borderStops.push(
        `${MARKER_FILL_COLOR} ${startPercentage}%`,
        `${MARKER_FILL_COLOR} ${endPercentage}%`
      );
      currentPosition = endPercentage;
    });

    if (currentPosition < 100) {
      borderStops.push(
        `var(--slider-rail-color, #EFF1F2) ${currentPosition}%`,
        `var(--slider-rail-color, #EFF1F2) 100%`
      );
    }

    return `linear-gradient(to right, ${borderStops.join(', ')})`;
  };

  const buildSliderMarks = (validMarkers, duration, formatTime) =>
    validMarkers.reduce((acc, marker) => {
      acc[marker / duration] = formatTime(marker);
      return acc;
    }, {});

  useEffect(() => {
    if (duration <= 0) {
      setSliderMarks({});
      setSliderHighlightGradient(DEFAULT_SLIDER_HIGHLIGHT);
      setSliderRailBorderGradient('none');
      return;
    }

    const validMarkers = markers.map(Number).filter(Number.isFinite);

    setSliderMarks(
      showMarkers ? buildSliderMarks(validMarkers, duration, formatTime) : {}
    );

    const highlightRanges = buildMarkerHighlightRanges(validMarkers, duration);
    setSliderHighlightGradient(
      createSliderHighlightGradient(highlightRanges, duration)
    );
    setSliderRailBorderGradient(buildBorderGradient(highlightRanges, duration));
  }, [duration, formatTime, markers, showMarkers]);

  const sliderTooltipFormatter = useCallback(
    value => formatTime(value * duration),
    [duration, formatTime]
  );

  const handleBuffer = () => {
    setBuffering(true);
  };

  const handleBufferEnd = () => {
    setBuffering(false);
  };

  const handleReady = useCallback(() => {
    setLoading(false);
    setBuffering(false);
  }, []);

  const playerContainerStyle = getPlayerContainerStyle(url, height, width);

  // Effect to measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (playerContainerRef.current) {
        setContainerWidth(playerContainerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, [width, height, url]);

  return (
    <ConfigProvider theme={{ components: { Typography: { fontSize: 16 } } }}>
      <Flex vertical gap={0} align="center">
        <div
          ref={playerContainerRef}
          role="button"
          tabIndex={0}
          onContextMenu={e => e.preventDefault()}
          style={playerContainerStyle}
        >
          <ReactPlayer
            key={playerKey}
            ref={playerRef}
            className="react-player"
            url={url}
            playing={playing}
            volume={volume}
            muted={muted}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onReady={handleReady}
            onBuffer={handleBuffer}
            onBufferEnd={handleBufferEnd}
            controls={false}
            height={height}
            width={width}
            onEnded={handleEnded}
          />
          {!playing && showOverlay && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
                zIndex: 1,
              }}
            >
              {Overlay}
            </div>
          )}
          {(loading || buffering) && url !== '' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2,
              }}
            >
              <Spin size="large" />
            </div>
          )}
        </div>
        {(!loading || feature === 'vod') && (
          <Flex
            gap={12}
            align="center"
            justify="space-between"
            style={{
              backgroundColor: 'var(--slider-bg-color, #000000)',
              padding: 12,
              width:
                containerWidth && fixedContentWidth ? containerWidth : '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Icon
              onClick={handlePlayPause}
              component={getPlayPauseIcon(playing, theme)}
              className="audio-player-icon"
            />
            <Text>{`${formatTime(currentTime)}/${formatTime(duration)}`}</Text>
            <Slider
              min={0}
              max={1}
              value={played}
              onChange={handleSeekChange}
              step={0.01}
              style={{
                width: sliderWidth,
                '--slider-highlight-gradient': sliderHighlightGradient,
                '--slider-played-ratio': Math.max(played, MIN_PLAYED_RATIO),
                '--video-marker-color': MARKER_FILL_COLOR,
                '--slider-rail-border-gradient': sliderRailBorderGradient,
              }}
              marks={showMarkers ? sliderMarks : undefined}
              tooltip={{
                formatter: sliderTooltipFormatter,
              }}
              className="video-player-slider"
            />
            {!disableVolume && (
              <Icon
                component={getVolumeIcon(muted, theme)}
                className="audio-player-icon"
                onClick={handleMute}
              />
            )}
            <Icon
              component={theme === DARK ? Replay : ReplayBlack}
              onClick={handleReplay}
              className="audio-player-icon"
            />
          </Flex>
        )}
      </Flex>
    </ConfigProvider>
  );
};

VideoPlayer.propTypes = {
  url: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sliderWidth: PropTypes.number,
  setIsPlayClicked: PropTypes.func,
  markers: PropTypes.array,
  showMarkers: PropTypes.bool,
  shouldPauseVideo: PropTypes.bool,
  showOverlay: PropTypes.bool,
  Overlay: PropTypes.node,
  feature: PropTypes.string,
  disableVolume: PropTypes.bool,
};

export default React.memo(VideoPlayer);
